import { Flex } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import WidgetCard from '../common/WidgetCard';
import CurrentSeason from './CurrentSeasonDetails';
import MonthProgress from './MonthProgress';

import useNotifyError from 'hooks/useNotifyError';
import useSeasonIndex from 'hooks/useSeasonIndex';

import { SeasonOverviewCommonWidgetFilters as SeasonOverviewCommonWidgetProps } from 'screens/season-overview/types';
import { getMonthDescription, getMonths, getMonthType } from './util';

const SeasonProgressWidget = ({
  country,
  seasonTypeId,
}: SeasonOverviewCommonWidgetProps) => {
  const { data: seasonIndexData, error } = useSeasonIndex(country!);
  const { t } = useTranslation();

  const selectedSeasonIndex = useMemo(
    () => seasonIndexData?.find((option) => option?.id === seasonTypeId),
    [seasonTypeId, seasonIndexData],
  );

  const months = useMemo(
    () =>
      getMonths(
        selectedSeasonIndex?.startDate as string,
        selectedSeasonIndex?.endDate as string,
      ),
    [selectedSeasonIndex],
  );

  const areSeasonDatesValid = useMemo(
    () =>
      (selectedSeasonIndex?.startDate?.toString() || '')?.length > 0 &&
      (selectedSeasonIndex?.endDate?.toString() || '')?.length > 0,
    [selectedSeasonIndex],
  );

  useNotifyError({
    error,
    msg: 'Could not fetch season index data, Please try again.',
  });

  return (
    <WidgetCard
      header={t('seasonOverview.widgets.seasonProgress.header')}
      className="season-progress"
    >
      <CurrentSeason
        seasonIndex={selectedSeasonIndex?.seasonCode as string}
        from={selectedSeasonIndex?.startDate as string}
        to={selectedSeasonIndex?.endDate as string}
      />
      {areSeasonDatesValid && (
        <Flex
          align="flex-start"
          gap={7}
          wrap={'wrap'}
          className="month-progress-wrapper"
        >
          {months?.map(({ start, end }, index) => (
            <MonthProgress
              key={index}
              start={start}
              end={end}
              type={getMonthType(start, end)}
              title={t(`seasonOverview.widgets.seasonProgress.nthmonth`, {
                no: index + 1,
              })}
              subtitle={getMonthDescription(getMonthType(start, end))}
            />
          ))}
        </Flex>
      )}
    </WidgetCard>
  );
};

export default SeasonProgressWidget;
